import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Markup from '../../../../atoms/Markup'
import useNormalizedConfig from '../../hooks/useNormalizedValues'

function RSSFeedItemCollapsibleBody ({
  item,
  body: _body,
  contentType = 'html'
}) {
  const body = useNormalizedConfig(item, { collapsibleHeight: 200, ..._body })
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [needsCollapse, setNeedsCollapse] = useState(false)
  const contentRef = useRef(null)

  const defaultHeight = body.config?.collapsibleHeight || 200

  const checkOverflow = useCallback(() => {
    if (!contentRef.current || !defaultHeight) {
      setNeedsCollapse(false)
      return
    }

    const contentHeight = contentRef.current.scrollHeight
    const shouldCollapse = contentHeight > defaultHeight

    setNeedsCollapse(shouldCollapse)
    if (!shouldCollapse) {
      setIsCollapsed(false)
    }
  }, [defaultHeight])

  useEffect(() => {
    checkOverflow()

    window.addEventListener('resize', checkOverflow)
    const resizeObserver = new ResizeObserver(() => checkOverflow())

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current)
    }

    return () => {
      window.removeEventListener('resize', checkOverflow)
      resizeObserver.disconnect()
    }
  }, [body.value, checkOverflow, defaultHeight])

  const toggleCollapse = (e) => {
    e.stopPropagation()
    setIsCollapsed(!isCollapsed)
  }

  if (!body.value?.length) return null

  return (
    <div className={clsx('flex-1', body.className)} style={body.style}>
      <div
        ref={contentRef}
        className={clsx('overflow-hidden transition-all', {
          relative: needsCollapse && isCollapsed
        })}
        style={{
          maxHeight: needsCollapse && isCollapsed ? `${defaultHeight}px` : 'none',
          overflow: 'hidden'
        }}
      >
        <Markup value={body.value} type={contentType} />
        {needsCollapse && isCollapsed && (
          <div
            className='absolute bottom-0 left-0 right-0 h-16'
            style={{
              background: 'linear-gradient(to bottom, transparent, #fff)',
              height: '1em'
            }}
          />
        )}
      </div>
      {needsCollapse && (
        <button
          onClick={toggleCollapse}
          className='opal-link font-size-xs cursor-pointer mt-2'
          style={{ background: 'none', border: 'none', padding: 0 }}
        >
          {isCollapsed ? 'Show more' : 'Show less'}
        </button>
      )}
    </div>
  )
}

RSSFeedItemCollapsibleBody.propTypes = {
  item: PropTypes.object.isRequired,
  body: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      accessor: PropTypes.string,
      className: PropTypes.string,
      style: PropTypes.object,
      collapsibleHeight: PropTypes.number
    })
  ]),
  contentType: PropTypes.oneOf(['html', 'markdown', 'plain'])
}

export default RSSFeedItemCollapsibleBody
